import { getCore } from 'cvat-core-wrapper';

export function createThumbnailLoader(
    fetchBlob: (frame: number) => Promise<Blob>,
    downscale: (blob: Blob) => Promise<string>,
    concurrency = 4,
): { load(frame: number): Promise<string>; dispose(): void } {
    const cache = new Map<number, Promise<string>>();
    const queue: (() => void)[] = [];
    let active = 0;

    const next = (): void => {
        if (active >= concurrency || !queue.length) return;
        active += 1;
        queue.shift()!();
    };

    const load = (frame: number): Promise<string> => {
        const cached = cache.get(frame);
        if (cached) return cached;
        const promise = new Promise<string>((resolve, reject) => {
            queue.push(() => {
                Promise.resolve().then(() => fetchBlob(frame)).then(downscale).then(resolve, reject).finally(() => {
                    active -= 1;
                    next();
                });
            });
            next();
        });
        // A failed frame must be retryable on the next scroll into view.
        promise.catch(() => cache.delete(frame));
        cache.set(frame, promise);
        return promise;
    };

    const dispose = (): void => {
        cache.forEach((p) => p.then((url) => URL.revokeObjectURL(url)).catch(() => {}));
        cache.clear();
        queue.length = 0;
    };

    return { load, dispose };
}

// Drawings are large; keep only a small JPEG per frame in memory.
export async function downscaleToObjectURL(blob: Blob, width = 184): Promise<string> {
    // decode straight to thumbnail size; a full-resolution bitmap per frame is tens of MB
    const bitmap = await createImageBitmap(blob, { resizeWidth: width, resizeQuality: 'low' });
    const canvas = document.createElement('canvas');
    canvas.width = width;
    // browsers that ignore resizeWidth still get a scaled draw here
    canvas.height = Math.max(1, Math.round(bitmap.height * (width / bitmap.width)));
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const small = await new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.8);
    });
    return URL.createObjectURL(small);
}

function frameFetcher(session: 'jobs' | 'reference_jobs', jobId: number): (frame: number) => Promise<Blob> {
    const core = getCore();
    return async (frame: number) => {
        const response = await core.server.request(`${core.config.backendAPI}/${session}/${jobId}/data`, {
            method: 'GET',
            params: { type: 'frame', number: frame, quality: 'compressed' },
            responseType: 'blob',
        });
        return response.data as Blob;
    };
}

// keeping loaders across strip mounts lets a strip come back without refetching. An evicted
// loader is only forgotten, never disposed: a mounted strip may still show its URLs.
export function createLoaderCache<L>(limit: number): (key: string, make: () => L) => L {
    const loaders = new Map<string, L>();
    return (key: string, make: () => L): L => {
        let loader = loaders.get(key);
        if (loader) {
            loaders.delete(key);
        } else {
            loader = make();
        }
        loaders.set(key, loader);
        if (loaders.size > limit) {
            loaders.delete(loaders.keys().next().value as string);
        }
        return loader;
    };
}

type ThumbnailLoader = ReturnType<typeof createThumbnailLoader>;
// thumbnails are a few KB each
const sharedLoaders = createLoaderCache<ThumbnailLoader>(64);

export function sharedThumbnailLoader(session: 'jobs' | 'reference_jobs', jobId: number): ThumbnailLoader {
    return sharedLoaders(`${session}:${jobId}`, () => (
        createThumbnailLoader(frameFetcher(session, jobId), (blob) => downscaleToObjectURL(blob))
    ));
}

export const jobFrameFetcher = (jobId: number): ReturnType<typeof frameFetcher> => frameFetcher('jobs', jobId);
export const referenceJobFrameFetcher = (jobId: number): ReturnType<typeof frameFetcher> => (
    frameFetcher('reference_jobs', jobId)
);
